import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { CircuitDetails, DevicesActions, RichDevice } from '@store';

import {
  BatteryInformationFeaturesType,
  DeviceRoles,
  DiagnosisData,
  NumberValue,
} from '@vi-iot/client/index';
import { DevicesSelectors } from './devices.selectors';

@Injectable({ providedIn: 'root' })
export class DevicesStore {
  loaded$ = this.store.select(DevicesSelectors.isLoaded);
  getSelectedDeviceId$ = this.store.select(DevicesSelectors.selectCurrentSelectedDeviceId);
  getSelectedDevice$ = this.store.select(DevicesSelectors.getSelectedDevice);
  areDevicesLoadedAndOneIsSelected$ = this.store.select(
    DevicesSelectors.areDevicesLoadedAndOneIsSelected
  );
  selectDeviceEntities$ = this.store.select(DevicesSelectors.selectDeviceEntities);
  isFloorHeatingThermostatAvailable$: Observable<boolean> = this.store.select(
    DevicesSelectors.isFloorHeatingThermostatAvailable()
  );
  isDhwAvailable$: Observable<{ isDhw: boolean }> = this.store.select(
    DevicesSelectors.isDhwAvailable()
  );
  isHeatingZoneAvailable$ = this.store.select(DevicesSelectors.isHeatingZoneAvailable());
  isSystemPanelVisible$ = this.store.select(DevicesSelectors.isSystemPanelVisible());
  selectedDevice$ = this.store.select(DevicesSelectors.getCurrentlySelectedDevice());
  devicesOfV2Cascade$ = this.store.select(DevicesSelectors.getDevicesOfV2Cascade());
  idsOfV2Cascade$ = this.store.select(DevicesSelectors.getDevicesIdsOfV2Cascade());
  idsOfVitoCharge$ = this.store.select(DevicesSelectors.getDevicesIdsOfVitoCharge());
  isV2Cascade$ = this.store.select(DevicesSelectors.isV2Cascade());
  trendingDevices$ = this.store.select(DevicesSelectors.selectTrendingDevices);
  mainDevices$: Observable<RichDevice[]> = this.store.select(DevicesSelectors.getMainDevices());
  heatingDevice$: Observable<RichDevice> = this.store.select(DevicesSelectors.getHeatingDevice());
  mainAndLagDevices$: Observable<RichDevice[]> = this.store.select(
    DevicesSelectors.getMainAndLagDevices()
  );
  mainDevicesForHeaderTiles$: Observable<RichDevice[]> = this.store.select(
    DevicesSelectors.mainDevicesForHeaderTiles
  );
  heatingSystemOverviewUrl$ = this.store.select(DevicesSelectors.getHeatingSystemOverviewUrl());
  heatPumpSmartGrid$ = this.store.select(DevicesSelectors.getHeatPumpSmartGridActualState());
  isVitovalorPA2Device$ = this.store.select(DevicesSelectors.isVitovalorPA2Device);
  isFuelCellDevice$ = this.store.select(DevicesSelectors.isFuelCellDevice());
  isESSDevice$ = this.store.select(DevicesSelectors.isESSDevice());
  isEcoWaterDevice$ = this.store.select(DevicesSelectors.isEcoWaterDevice);
  isEcotronicDevice$ = this.store.select(DevicesSelectors.isEcotronicDevice);
  isWallboxDevice$ = this.store.select(DevicesSelectors.isSelectedDeviceWallbox);
  isFullCascadeMemberIds$ = this.store.select(DevicesSelectors.selectIsFullCascadeMemberIds);
  isCommonDevice$: Observable<boolean> = this.store.select(DevicesSelectors.isCommonDevice);
  isLegacyDeviceWithoutBoilerSerial$ = this.store.select(
    DevicesSelectors.isLegacyDeviceWithoutBoilerSerial()
  );
  isMultifamily$: Observable<boolean> = this.store.select(DevicesSelectors.isMultiFamily());

  isStandaloneDevice$ = this.store.select(DevicesSelectors.isStandaloneDevice);

  isHemsActivated$ = this.store.select(DevicesSelectors.isHemsActivated());

  getAllWallboxDevicesWithMatchingBoilerSerial$ = this.store.select(
    DevicesSelectors.getAllWallboxDevicesWithMatchingBoilerSerial
  );

  getIsStoreAvailableForSrc$ = this.store.select(DevicesSelectors.getIsStoreAvailableForSrc());

  mainDeviceBoilerSerial$ = this.store.select(DevicesSelectors.getMainDeviceBoilerSerial);
  isRemoteControllerAvailable$ = this.store.select(DevicesSelectors.isRemoteControllerAvailable());
  getRemoteControllerDevices$ = this.store.select(DevicesSelectors.getRemoteControllerDevices());
  isRemoteControllerOrClimateSensorAvailable$ = this.store.select(
    DevicesSelectors.isRemoteControllerOrClimateSensorAvailable()
  );
  isLowBudgetDevice$ = this.store.select(DevicesSelectors.isLowBudgetDevice);
  getSolarLogInverter$ = this.store.select(DevicesSelectors.getSolarLogInverter);
  getSolarLogGateway$ = this.store.select(DevicesSelectors.getSolarLogGateway);
  getPhotovoltaicDevice$ = this.store.select(DevicesSelectors.getPhotovoltaicDevice);
  getDevicesOfSelectedGateway$ = this.store.select(DevicesSelectors.getDevicesOfSelectedGateway());
  deviceTimeOffset$ = this.store.select(DevicesSelectors.deviceTimeOffset());
  shouldUseConsolidatedFeature$ = this.store.select(
    DevicesSelectors.selectShouldUseConsolidatedFeature()
  );

  selectBatteryModuleCategory$ = this.store.select(DevicesSelectors.selectBatteryModuleCategory());
  selectBatteryWithMostValues$ = this.store.select(DevicesSelectors.selectBatteryWithMostValues());
  selectFeatureForEnergyStorageDataTrending$ = this.store.select(
    DevicesSelectors.selectFeatureForEnergyStorageDataTrending()
  );
  selectVitoChargeDevices$ = this.store.select(
    DevicesSelectors.selectDeviceByRole(DeviceRoles.ProductVitocharge)
  );
  vitotrolConnectedToGateway$ = this.store.select(
    DevicesSelectors.selectVitotrolConnectedToGateway
  );

  dryContactDevices$ = this.store.select(DevicesSelectors.selectDryContactDevices);
  dryContactHeatPump$ = this.store.select(DevicesSelectors.selectDryContactHeatPump);
  dryContactGeneric$ = this.store.select(DevicesSelectors.selectDryContactGenericDevices);

  constructor(private store: Store) {}

  selectDeviceIdByDeviceSerial(deviceSerial: string): Observable<string | undefined> {
    return this.store.select(DevicesSelectors.selectDeviceIdByDeviceSerial(deviceSerial));
  }

  isFeatureWithPendingUpdates(featureName: string): Observable<boolean> {
    return this.store.select(DevicesSelectors.isFeatureWithPendingUpdates(featureName));
  }

  selectGatewayOnUserClick(gatewaySerial: string, deviceId: string, selectId: string): void {
    this.store.dispatch(
      DevicesActions.setSelectedDeviceOnUserClick({ gatewaySerial, deviceId, selectId })
    );
  }

  isVentilationDevice$ = this.store.select(
    DevicesSelectors.selectIsSelectedDeviceVentilationDevice
  );

  isSelectedDeviceWithError$ = this.store.select(DevicesSelectors.isSelectedDeviceWithError());

  updateDevices(): void {
    this.store.dispatch(DevicesActions.updateDevices());
  }

  updateBmuSerial(bmuSerial: string): void {
    this.store.dispatch(
      DevicesActions.updateBmuSerial({
        bmuSerial,
      })
    );
  }

  updateBoilerSerial(boilerSerial: string): void {
    this.store.dispatch(
      DevicesActions.updateBoilerSerial({
        boilerSerial,
      })
    );
  }

  isE3HeatpumpDevice(dev?: RichDevice): Observable<boolean> {
    return this.store.select(DevicesSelectors.isE3HeatpumpDevice(dev));
  }

  isE3Device(): Observable<boolean> {
    return this.store.select(DevicesSelectors.isE3Device());
  }

  getBatteryName(id: number): Observable<string> {
    return this.store.select(DevicesSelectors.getBatteryName(id));
  }

  getBatteryInformation(
    id: number,
    param: BatteryInformationFeaturesType
  ): Observable<NumberValue> {
    return this.store.select(DevicesSelectors.getBatteryInformation(id, param));
  }

  getNormalProgramTemperature(circuitId: string): Observable<number> {
    return this.store.select(DevicesSelectors.getNormalProgramTemperature(circuitId));
  }

  setSelectedDevice(deviceSelectId: string): void {
    this.store.dispatch(DevicesActions.setSelectedDeviceById({ deviceSelectId }));
  }

  setSelectedDeviceWithoutFetchingFeatures(deviceSelectId: string): void {
    this.store.dispatch(
      DevicesActions.setSelectedDeviceWithoutFetchingFeatures({ deviceSelectId })
    );
  }

  resetSelectedDeviceId(): void {
    this.store.dispatch(DevicesActions.resetSelectedDeviceId());
  }

  switchSelectionToMainDevice(): void {
    this.store.dispatch(DevicesActions.switchSelectionToMainDevice());
  }

  getTopDevicesOfV2Cascade(topDevicesLimit: number): Observable<RichDevice[]> {
    return this.store.select(DevicesSelectors.getDevicesOfV2Cascade(topDevicesLimit));
  }

  getCascadeDevicesNameForSpecificGateway(gatewaySerial: string): Observable<string[]> {
    return this.store.select(
      DevicesSelectors.getCascadeDevicesNameForSpecificGateway(gatewaySerial)
    );
  }

  getCascadeDeviceById(deviceId: string): Observable<RichDevice> {
    return this.store.select(DevicesSelectors.getCascadeDeviceById(deviceId));
  }

  isFuelCellDevice(deviceId: string): Observable<boolean> {
    return this.store.select(DevicesSelectors.isFuelCellDevice(deviceId));
  }

  isESSDevice(deviceId: string): Observable<boolean> {
    return this.store.select(DevicesSelectors.isESSDevice(deviceId));
  }

  getAvailableCircuitsDetails(deviceId: string): Observable<CircuitDetails[]> {
    return this.store.select(DevicesSelectors.getAvailableCircuitsDetails(deviceId));
  }

  getCallForHeatHeatingZoneIndex(deviceId: string): Observable<{ index: string }> {
    return this.store.select(DevicesSelectors.getCallForHeatHeatingZoneIndex(deviceId));
  }

  getDeviceId(boilerSerial: string): Observable<string> {
    return this.store.select(DevicesSelectors.getDeviceId(boilerSerial));
  }

  getDeviceBusinessName(): Observable<string> {
    return this.store.select(DevicesSelectors.selectDeviceBusinessName());
  }

  getCascadeMemberId(deviceId: string): Observable<number> {
    return this.store.select(DevicesSelectors.getCascadeMemberId(deviceId));
  }

  selectAllSolarLogPvImmersionHeater(): Observable<RichDevice[]> {
    return this.store.select(DevicesSelectors.selectAllSolarLogPvImmersionHeater());
  }

  isInternalHydraulicAvailable(deviceId: string): Observable<boolean> {
    return this.store.select(DevicesSelectors.isInternalHydraulicAvailable(deviceId));
  }

  updateDevicesWithDiagnosticData(): void {
    this.store.dispatch(DevicesActions.loadDiagnosticDataByDevices.attempt());
  }

  selectAnomalyListForDevice(boilerSerial: string): Observable<DiagnosisData[]> {
    return this.store.select(DevicesSelectors.selectAnomalyListForDevice(boilerSerial));
  }

  updateBatteryHasLargestModules(): void {
    this.store.dispatch(DevicesActions.loadBatteryModuleCategories.attempt());
  }

  isAnySRCDeviceAssignedToHeatingCircuit(
    circuitId: number,
    roomControlId: string
  ): Observable<boolean> {
    return this.store.select(
      DevicesSelectors.selectIsAnySRCDeviceAssignedToHeatingCircuitWithSRCActive(
        circuitId,
        roomControlId
      )
    );
  }

  isHeatingDeviceTimeAvailable(deviceId?: string): Observable<boolean> {
    return this.store.select(DevicesSelectors.isHeatingDeviceTimeAvailable(deviceId));
  }
}
